import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SideBar from './SideBar';
import TopBar from './TopBar';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaTruck, FaCheckCircle, FaTimesCircle, FaBox, FaKey } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { API_URL } from './config';

const DriverConfirmDispatch = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [secondCollectionCode, setSecondCollectionCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [items, setItems] = useState([]);
    const [totalValue, setTotalValue] = useState(0);
    const [receivedQuantities, setReceivedQuantities] = useState({});
    const [remainders, setRemainders] = useState({});
    const [tripId, setTripId] = useState('');
    const [companyId, setCompanyId] = useState('');
    const [branchId, setBranchId] = useState({});
    const [selectedProducts, setSelectedProducts] = useState({});
    const [goodsFound, setGoodsFound] = useState(false); // New state variable for goods found

    useEffect(() => {
        if (!location.state || !location.state.items) {
            navigate('/dispatch');
            return;
        }

        setItems(location.state.items);
        setTripId(location.state.tripId);
        setCompanyId(location.state.companyId);
        setBranchId(location.state.branchId);

        const initialReceivedQuantities = location.state.items.reduce((acc, item) => {
            acc[item.id] = 0; // Default to 0 for received quantities
            return acc;
        }, {});
        setReceivedQuantities(initialReceivedQuantities);

        const initialRemainders = location.state.items.reduce((acc, item) => {
            acc[item.id] = 0; // Default to 0 for remainders
            return acc;
        }, {});
        setRemainders(initialRemainders);
    }, [location.state, navigate]);

    const calculateTotalValue = () => {
        const total = items.reduce((sum, item) => {
            const quantityCollected = receivedQuantities[item.id] || 0;
            return sum + (item.unitCost * quantityCollected);
        }, 0);
        setTotalValue(total);
    };

    const handleQuantityChange = (id, value) => {
        setReceivedQuantities(prev => ({ ...prev, [id]: value }));
        calculateTotalValue();
    };

    const handleRemainderChange = (id, value) => {
        setRemainders(prev => ({ ...prev, [id]: value }));
    };

    const handleCheckboxChange = (item) => {
        const quantityCollected = receivedQuantities[item.id];
        if (quantityCollected <= 0) {
            setError('Please enter a quantity to collect before selecting this item.');
            return;
        }

        setError(''); // Clear any previous error

        setSelectedProducts(prev => {
            const newSelected = { ...prev };
            if (newSelected[item.id]) {
                delete newSelected[item.id];
            } else {
                newSelected[item.id] = {
                    ...item,
                    quantityCollected: quantityCollected,
                };
            }
            localStorage.setItem('productsToBeDispatched', JSON.stringify(Object.values(newSelected)));
            console.log('Selected Products:', Object.values(newSelected));
            return newSelected;
        });
    };

    const handleConfirmCollection = async () => {
        if (!secondCollectionCode.trim()) {
            setError('Please fill in the second collection confirmation code.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call

            if (secondCollectionCode === '1234') { // Replace with actual validation logic
                setSuccess(true);
                setTimeout(() => {
                    navigate('/dispatch');
                }, 2000);
            } else {
                setError('Invalid collection code. Please try again.');
            }
        } catch {
            setError('An error occurred while confirming collection. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCheckGoods = async () => {
        const products = Object.values(selectedProducts);
        let foundGoods = false; // Track if any goods are found

        for (const product of products) {
            const response = await fetch(`${API_URL}/salelist/getdispatchgoodstoupdate/${companyId}/${branchId}/${tripId}/${product.id}/${secondCollectionCode}`);
            const data = await response.json();

            const found = data.some(item => item.product_id === product.id);
            if (found) {
                foundGoods = true; // Set to true if goods are found
                const result = await Swal.fire({
                    title: 'Goods Found',
                    text: 'Are you ready for dispatch?',
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No',
                });
                if (result.isConfirmed) {
                    console.log('User is ready for dispatch.');
                } else {
                    console.log('User is not ready for dispatch.');
                }
                break; // Exit after first found product
            }
        }

        setGoodsFound(foundGoods); // Update state based on whether goods were found

        // If no products were found
        if (!foundGoods) {
            Swal.fire({
                title: 'No Matching Goods',
                text: 'No goods found for the selected items.',
                icon: 'info',
            });
        }
    };

    return (
        <div className="d-flex flex-column min-vh-100" style={{ backgroundColor: '#fff' }}>
            <TopBar />
            <div className="d-flex flex-grow-1">
                <SideBar />
                <div className="flex-grow-1 p-4" style={{ backgroundColor: '#fff', overflowY: 'auto', maxHeight: 'calc(100vh - 56px)', marginLeft: '250px' }}>
                    <div className="container">
                        <div className="row mb-4" style={{ marginTop: '60px' }}>
                            <div className="col">
                                <h2 className="d-flex align-items-center">
                                    <FaTruck className="me-2" /> Driver Collection Confirmation
                                </h2>
                            </div>
                        </div>

                        {success ? (
                            <div className="card shadow-sm mb-4">
                                <div className="card-body text-center py-5">
                                    <FaCheckCircle className="text-success mb-3" size={50} />
                                    <h3>Collection Confirmed Successfully!</h3>
                                    <p>Redirecting to dispatch page...</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="card shadow-sm mb-4">
                                    <div className="card-header" style={{ backgroundColor: '#DAA520', color: '#fff' }}>
                                        <h5 className="mb-0">
                                            <FaBox className="me-2" /> Items for Collection
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="table-responsive">
                                            <table className="table table-hover">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Confirm</th>
                                                        <th>Product ID</th>
                                                        <th>Product Code</th>
                                                        <th>Product name</th>
                                                        <th>Unit Cost</th>
                                                        <th>Quantity</th>
                                                        <th>Collected Quantity</th>
                                                        <th>Remaining Quantity</th>
                                                        <th>Total</th>
                                                        <th>Trip ID</th>
                                                        <th>Company ID</th>
                                                        <th>Branch ID</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {items.map((item) => (
                                                        <tr key={item.id}>
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    onChange={() => handleCheckboxChange(item)}
                                                                />
                                                            </td>
                                                            <td>{item.id}</td>
                                                            <td>{item.productCode}</td>
                                                            <td>{item.productName}</td>
                                                            <td>${(item.unitCost || 0).toFixed(2)}</td>
                                                            <td>{item.quantity}</td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    value={receivedQuantities[item.id] || 0}
                                                                    onChange={(e) => handleQuantityChange(item.id, Number(e.target.value))}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    value={remainders[item.id] || 0}
                                                                    onChange={(e) => handleRemainderChange(item.id, Number(e.target.value))}
                                                                />
                                                            </td>
                                                            <td>${((item.unitCost || 0) * (receivedQuantities[item.id] || 0)).toFixed(2)}</td>
                                                            <td>{tripId}</td>
                                                            <td>{companyId}</td>
                                                            <td>{branchId}</td>
                                                        </tr>
                                                    ))}
                                                    <tr className="table-primary">
                                                        <td colSpan="8" className="text-end fw-bold">Total Value:</td>
                                                        <td className="fw-bold">${totalValue.toFixed(2)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <div className="card shadow-sm mb-4">
                                    <div className="card-body">
                                        <div className="form-group">
                                            <label className="form-label d-flex align-items-center">
                                                <FaKey className="me-2" /> Second Collection Confirmation Code
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${error ? 'is-invalid' : ''}`}
                                                placeholder="Enter second collection confirmation code"
                                                value={secondCollectionCode}
                                                onChange={(e) => setSecondCollectionCode(e.target.value)}
                                            />
                                            {error && (
                                                <div className="invalid-feedback d-block">
                                                    <FaTimesCircle className="me-2" />
                                                    {error}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            className="btn"
                                            style={{ backgroundColor: '#DAA520', color: '#fff' }}
                                            onClick={handleCheckGoods}
                                            disabled={isSubmitting}
                                        >
                                            <FaCheckCircle className="me-2" /> Check Goods
                                        </button>
                                        {goodsFound && ( // Conditionally render the Confirm Collection button
                                            <button
                                                className="btn ms-2"
                                                style={{ backgroundColor: '#DAA520', color: '#fff' }}
                                                onClick={handleConfirmCollection}
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Confirming...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaCheckCircle className="me-2" /> Confirm Collection
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DriverConfirmDispatch;